export function normalizeEvidenceText(value) {
    return String(value || '')
        .normalize('NFKD')
        .replace(/[’‘]/g, "'")
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .toLowerCase()
}

export function normalizeEvidenceTextStrict(value) {
    return String(value || '')
        .normalize('NFKD')
        .replace(/[’‘]/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
}

export function findUniqueEvidenceMatch(textItems, highlightTexts) {
    const items = (textItems || [])
        .map((item, originalIndex) => ({ ...item, originalIndex, normalized: normalizeEvidenceText(item?.str) }))
        .filter(item => item.normalized)
    const targets = Array.from(new Map((highlightTexts || []).slice(0, 12)
        .map(value => String(value || '').slice(0, 512))
        .map(value => ({ normalized: normalizeEvidenceText(value), strict: normalizeEvidenceTextStrict(value) }))
        .filter(target => target.normalized.length >= 4 || (target.normalized.length >= 2 && target.strict.length >= 4))
        .map(target => [target.strict, target])).values())
    if (!targets.length) return { status: 'page_only', indexes: [], target: '' }

    // Targets are ordered from the occurrence's own text to fallback context.
    // An ambiguous occurrence must never be replaced by an unrelated broad match.
    for (const target of targets) {
        const candidates = []
        for (let start = 0; start < items.length; start += 1) {
            let combinedOriginal = ''
            let lineCount = 1
            for (let end = start; end < Math.min(items.length, start + 24); end += 1) {
                if (end > start && !sameTextLine(textItemLineY(items[end - 1]), textItemLineY(items[end]))) {
                    if (!adjacentWrappedTextLine(items[end - 1], items[end]) || ++lineCount > 3) break
                }
                combinedOriginal = `${combinedOriginal} ${items[end].str || ''}`.trim()
                const combined = normalizeEvidenceText(combinedOriginal)
                const exact = combined === target.normalized
                const strictExact = exact && normalizeEvidenceTextStrict(combinedOriginal) === target.strict
                if (exact || containsNormalizedPhrase(combined, target.normalized)) candidates.push({
                    target: target.normalized, strictExact,
                    score: strictExact ? 4 : exact ? 3 : 2,
                    indexes: items.slice(start, end + 1).map(item => item.originalIndex)
                })
                if (combined.length > Math.max(target.normalized.length * 2, target.normalized.length + 80)) break
            }
        }
        if (!candidates.length) continue
        const bestScore = Math.max(...candidates.map(candidate => candidate.score))
        const scored = dedupeCandidates(candidates.filter(candidate => candidate.score === bestScore))
        // Remove expanded matches of the same occurrence, but retain independent
        // repeats even when one wraps across more text items than another.
        const best = scored.filter(candidate => !scored.some(other =>
            other !== candidate && other.indexes.length < candidate.indexes.length &&
            other.indexes.every(index => candidate.indexes.includes(index))
        ))
        if (best.length !== 1) return { status: 'ambiguous', indexes: [], target: target.normalized }
        return { status: best[0].strictExact ? 'exact' : 'normalized', indexes: best[0].indexes, target: best[0].target }
    }
    return { status: 'not_found', indexes: [], target: targets[0].normalized }
}

function adjacentWrappedTextLine(previous, next) {
    const previousTransform = Array.isArray(previous?.transform) ? previous.transform : []
    const nextTransform = Array.isArray(next?.transform) ? next.transform : []
    if (previousTransform.length < 6 || nextTransform.length < 6) return false
    const verticalGap = Number(previousTransform[5]) - Number(nextTransform[5])
    const height = Math.max(Number(previous.height || 0), Number(next.height || 0), Math.abs(Number(previousTransform[3] || 0)), Math.abs(Number(nextTransform[3] || 0)))
    const startsNewColumn = Number(nextTransform[4]) > Number(previousTransform[4]) + 24
    return verticalGap > 2.5 && verticalGap <= Math.min(40, Math.max(24, height * 2.2)) && !startsNewColumn
}

export function resolvePdfPageNumber(source) {
    const structuredPage = Number(source?.pageNumber || source?.renderedPage || 0)
    if (structuredPage > 0) return structuredPage
    const pageMatch = String(source?.location || '').match(/\b(?:PDF\s+)?page\s+(\d+)\b/i)
    return pageMatch ? Number(pageMatch[1]) : 1
}

export function findSourceNavigationEntry(entries, {
    fileId,
    fileName,
    location,
    sourceLocation,
    itemId
} = {}) {
    const requestedPage = Number(sourceLocation?.pageNumber || 0)
    const index = (entries || []).findIndex(entry =>
        (!itemId || entry.itemId === itemId) &&
        entry.fileId === fileId &&
        entry.fileName === fileName &&
        entry.location === location &&
        (!requestedPage || Number(entry.pageNumber || 0) === requestedPage)
    )

    return {
        index,
        entry: index >= 0 ? entries[index] : null
    }
}

export function sourceHighlightTexts(sourceLocation, item) {
    return Array.from(new Set([
        sourceLocation?.matchText,
        sourceLocation?.contextText,
        item?.kind === 'wording' ? item?.evidence?.highlightTexts?.[0] : '',
        sourceLocation?.snippet
    ]
        .map(value => String(value || '').trim().slice(0, 512))
        .filter(value => value.length >= 4)))
}

export function sourceLocationQuality(source) {
    if (source?.worksheet && source?.cellRef) return 'exact'
    if (source?.heading || source?.noteLabel || source?.lineNumber) return 'exact'
    if (source?.paragraphIndex && (source?.kind === 'docx_paragraph' || !Number(source?.pageNumber || source?.renderedPage || 0))) return 'exact'
    if (Number(source?.pageNumber || source?.renderedPage || 0) > 0 || /\b(?:PDF\s+)?page\s+\d+\b/i.test(String(source?.location || source?.label || ''))) return 'page'
    return 'document'
}

export function preserveSessionSourceEvidence(previousResult, nextResult) {
    if (!previousResult || !nextResult) return nextResult
    if (previousResult.reviewId && nextResult.reviewId && previousResult.reviewId !== nextResult.reviewId) {
        return nextResult
    }

    const previousFiles = indexFiles(previousResult.fileResults, 'name')
    const fileResults = (nextResult.fileResults || []).map(file => {
        const previousFile = previousFiles.get(`id:${file.fileId}`) || previousFiles.get(`name:${file.name}`)
        if (!previousFile) return file
        return {
            ...file,
            findings: mergeFindingEvidence(file.findings, previousFile.findings)
        }
    })

    const previousWordingFiles = indexFiles(previousResult?.wordingReview?.files, 'fileName')
    const wordingReview = {
        ...(nextResult.wordingReview || {}),
        files: (nextResult?.wordingReview?.files || []).map(file => {
            const previousFile = previousWordingFiles.get(`id:${file.fileId}`) || previousWordingFiles.get(`name:${file.fileName}`)
            if (!previousFile) return file
            return {
                ...file,
                findings: (file.findings || []).map(finding => {
                    const previousFinding = findMatchingFinding(previousFile.findings, finding)
                    if (!previousFinding) return finding
                    return {
                        ...finding,
                        originalText: finding.originalText || previousFinding.originalText || '',
                        proposedText: finding.proposedText || previousFinding.proposedText || '',
                        locations: mergeLocationEvidence(finding.locations, previousFinding.locations)
                    }
                })
            }
        })
    }

    return {
        ...nextResult,
        fileResults,
        wordingReview
    }
}

function indexFiles(files, nameKey) {
    const output = new Map()
    for (const file of files || []) {
        if (file.fileId) output.set(`id:${file.fileId}`, file)
        if (file[nameKey]) output.set(`name:${file[nameKey]}`, file)
    }
    return output
}

function mergeFindingEvidence(findings, previousFindings) {
    return (findings || []).map(finding => {
        const previousFinding = findMatchingFinding(previousFindings, finding)
        if (!previousFinding) return finding
        return {
            ...finding,
            locations: mergeLocationEvidence(finding.locations, previousFinding.locations)
        }
    })
}

function findMatchingFinding(findings, finding) {
    return (findings || []).find(candidate =>
        (finding.id && candidate.id === finding.id) ||
        (finding.reviewStateKey && candidate.reviewStateKey === finding.reviewStateKey) ||
        (finding.itemId && candidate.itemId === finding.itemId)
    )
}

function mergeLocationEvidence(locations, previousLocations) {
    return (locations || []).map((location, index) => {
        const indexedLocation = previousLocations?.[index]
        const previous = indexedLocation && sameSourceLocation(indexedLocation, location)
            ? indexedLocation
            : (previousLocations || []).find(candidate => sameSourceLocation(candidate, location))
        if (!previous) return location
        return {
            ...location,
            matchText: location.matchText || previous.matchText || '',
            contextText: location.contextText || previous.contextText || '',
            snippet: location.snippet || previous.snippet || ''
        }
    })
}

function sameSourceLocation(left = {}, right = {}) {
    return [
        'kind',
        'pageNumber',
        'displayPageNumber',
        'paragraphIndex',
        'lineNumber',
        'heading',
        'noteLabel',
        'worksheet',
        'cellRef'
    ].every(key => String(left?.[key] || '') === String(right?.[key] || ''))
}

function textItemLineY(item) {
    const transform = Array.isArray(item?.transform) ? item.transform : []
    return Number(transform[5] || 0)
}

function sameTextLine(left, right) {
    return Math.abs(Number(left || 0) - Number(right || 0)) <= 2.5
}

function containsNormalizedPhrase(value, target) {
    if (!value || !target || value === target) return false
    if (target.split(/\s+/).length !== 1) return false
    return ` ${value} `.includes(` ${target} `)
}

function dedupeCandidates(candidates) {
    const seen = new Set()
    return candidates.filter(candidate => {
        const key = candidate.indexes.join(':')
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}
